import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listSisStoreLabels, saveSisStoreLabels } from '@/api/sisStock';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Restrict the list to one group; blank shows all SIS stores. */
  group?: string;
}

/**
 * Configure each SIS store's partner feed label — the value that appears in the
 * store's own group-wise report (e.g. "MONALISA UNIT-7") that maps a feed row
 * back to our store. Multiple labels per store are comma-separated.
 */
export function StoreLabelsModal({ open, onClose, group }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [labels, setLabels] = useState<Record<number, string>>({});

  const query = useQuery({
    queryKey: ['sis-store-labels', group || ''],
    queryFn: () => listSisStoreLabels(group || undefined),
    enabled: open,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.data) setLabels(Object.fromEntries(query.data.map((s) => [s.partyId, s.reportLabel])));
  }, [query.data]);

  const mut = useMutation({
    mutationFn: () =>
      saveSisStoreLabels(
        (query.data ?? []).map((s) => ({ partyId: s.partyId, reportLabel: labels[s.partyId] ?? '' })),
      ),
    onSuccess: (r) => {
      toast.push(`Saved ${r.updated} store label${r.updated !== 1 ? 's' : ''}`, 'success');
      qc.invalidateQueries({ queryKey: ['sis-store-labels'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Could not save labels'), 'error'),
  });

  const rows = query.data ?? [];

  return (
    <Modal
      open={open}
      title="Store Report Labels"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => mut.mutate()} disabled={mut.isPending || rows.length === 0}>
            {mut.isPending ? 'Saving…' : 'Save labels'}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-xs text-[var(--text-muted)]">
        Set how each store appears in its group-wise report file (the <strong>Store</strong> column). Use the
        partner's own label — e.g. <em>MONALISA UNIT-7</em>. Separate multiple labels with commas. Store name
        and code always match too.
      </p>
      {query.isFetching && rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-[var(--text-muted)]">Loading stores…</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left" style={{ color: 'var(--text-muted)' }}>
                <th className="px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wider">Store</th>
                <th className="px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wider">Group</th>
                <th className="px-2 py-1.5 text-[10px] font-extrabold uppercase tracking-wider">Report Label(s)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.partyId} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td className="px-2 py-1.5">
                    <div className="font-semibold text-[var(--text-primary)]">{s.name}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">{s.code}{s.city ? ` · ${s.city}` : ''}</div>
                  </td>
                  <td className="px-2 py-1.5 text-[var(--text-secondary)]">{s.group || '—'}</td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={labels[s.partyId] ?? ''}
                      onChange={(e) => setLabels((p) => ({ ...p, [s.partyId]: e.target.value }))}
                      placeholder="e.g. MONALISA UNIT-7"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
